import { useState, useRef, useEffect, useCallback } from 'react';

interface TranscriptSegment {
    time: number;
    text: string;
}

interface RecordingPlayerProps {
    leadId: string | number;
    apiBase: string;
    authHeaders: Record<string, string>;
    /** '/leads' for client, '/admin' for admin */
    routePrefix: string;
}

function formatTimestamp(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function RecordingPlayer({ leadId, apiBase, authHeaders, routePrefix }: RecordingPlayerProps) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);
    const [audioDownloadName, setAudioDownloadName] = useState(`lead-${leadId}-recording.wav`);
    const [audioError, setAudioError] = useState<string | null>(null);
    const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [transcriptError, setTranscriptError] = useState<string | null>(null);
    const [activeSegmentIndex, setActiveSegmentIndex] = useState(-1);
    const [playbackRate, setPlaybackRate] = useState(1);
    const hasAutoGenerated = useRef(false);

    // Build audio and transcription URLs correctly.
    // Admin routes: audio is at /admin/leads/:id/audio, transcribe is at /admin/transcribe
    // Client routes: audio is at /leads/:id/audio, transcribe is at /leads/transcribe
    const isAdmin = routePrefix === '/admin';
    const audioSrc = isAdmin
        ? `${apiBase}/admin/leads/${leadId}/audio`
        : `${apiBase}${routePrefix}/${leadId}/audio`;
    const transcribeUrl = `${apiBase}${routePrefix}/transcribe`;
    const authHeaderKey = JSON.stringify(authHeaders);

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;

        async function loadAudio() {
            setAudioError(null);
            setAudioObjectUrl(null);

            try {
                const response = await fetch(audioSrc, {
                    headers: { ...authHeaders },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error(`Unable to load recording (${response.status})`);
                }

                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                const extension = blob.type === 'audio/mpeg' ? 'mp3' : blob.type === 'audio/wav' || blob.type === 'audio/x-wav' ? 'wav' : 'audio';

                if (!cancelled) {
                    setAudioObjectUrl(objectUrl);
                    setAudioDownloadName(`lead-${leadId}-recording.${extension}`);
                }
            } catch (error) {
                if (!cancelled) {
                    setAudioError(error instanceof Error ? error.message : 'Unable to load recording');
                }
            }
        }

        loadAudio();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [audioSrc, authHeaderKey]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            audio.play().catch((error) => {
                setAudioError(error instanceof Error ? error.message : 'Unable to play recording');
            });
        } else {
            audio.pause();
        }
    }, []);

    const skipTime = useCallback((seconds: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + seconds));
    }, []);

    const seekTo = useCallback((time: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = time;
        audio.play().catch((error) => {
            setAudioError(error instanceof Error ? error.message : 'Unable to play recording');
        });
    }, []);

    const cycleSpeed = useCallback(() => {
        const speeds = [1, 1.25, 1.5, 2, 0.75];
        const nextIndex = (speeds.indexOf(playbackRate) + 1) % speeds.length;
        const newRate = speeds[nextIndex];
        setPlaybackRate(newRate);
        if (audioRef.current) audioRef.current.playbackRate = newRate;
    }, [playbackRate]);

    const generateTranscript = useCallback(async () => {
        setTranscriptLoading(true);
        setTranscriptError(null);

        try {
            const formData = new FormData();
            formData.append('lead_id', String(leadId));

            const response = await fetch(transcribeUrl, {
                method: 'POST',
                headers: {
                    ...authHeaders,
                },
                credentials: 'include',
                body: formData,
            });

            const contentType = response.headers.get('content-type') || '';
            const responseText = await response.text();
            let data: any = null;

            if (contentType.includes('application/json')) {
                try {
                    data = responseText ? JSON.parse(responseText) : {};
                } catch {
                    throw new Error('Transcription returned an invalid response. Please try again.');
                }
            }

            if (!response.ok) {
                if (data?.error) {
                    throw new Error(data.error);
                }

                throw new Error('Transcript is unavailable right now. The recording can still be played.');
            }

            if (!data) {
                throw new Error('Transcript is unavailable right now. The recording can still be played.');
            }

            let segments: TranscriptSegment[] = [];
            if (data.segments && data.segments.length > 0) {
                segments = data.segments.map((seg: { start: number; text: string }) => ({
                    time: seg.start,
                    text: seg.text.trim(),
                }));
            } else if (data.text) {
                segments = [{ time: 0, text: data.text }];
            }

            setTranscript(segments);
        } catch (error) {
            setTranscriptError(error instanceof Error ? error.message : 'Transcription failed');
        } finally {
            setTranscriptLoading(false);
        }
    }, [audioSrc, apiBase, authHeaders]);

    // Auto-generate transcript on mount
    useEffect(() => {
        if (!hasAutoGenerated.current) {
            hasAutoGenerated.current = true;
            generateTranscript();
        }
    }, [generateTranscript]);

    // Audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => setIsPlaying(false);
        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onError = () => setAudioError('Unable to play recording');

        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('error', onError);

        return () => {
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('error', onError);
        };
    }, []);

    // Highlight active transcript segment
    useEffect(() => {
        if (transcript.length === 0) return;

        let activeIdx = -1;
        for (let i = transcript.length - 1; i >= 0; i--) {
            if (currentTime >= transcript[i].time) {
                activeIdx = i;
                break;
            }
        }

        if (activeIdx !== activeSegmentIndex) {
            setActiveSegmentIndex(activeIdx);

            // Auto-scroll
            if (activeIdx >= 0 && transcriptRef.current) {
                const lines = transcriptRef.current.querySelectorAll('[data-segment]');
                const activeLine = lines[activeIdx] as HTMLElement;
                if (activeLine) {
                    const container = transcriptRef.current;
                    const lineTop = activeLine.offsetTop - container.offsetTop;
                    const containerHeight = container.clientHeight;
                    const scrollTop = container.scrollTop;

                    if (lineTop < scrollTop || lineTop + activeLine.offsetHeight > scrollTop + containerHeight) {
                        container.scrollTo({
                            top: lineTop - containerHeight / 3,
                            behavior: 'smooth',
                        });
                    }
                }
            }
        }
    }, [currentTime, transcript, activeSegmentIndex]);

    // Progress bar percentage
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className="mb-10">
            <h2 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Call Recording</h2>
            <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-6 md:p-8 shadow-sm">
                {/* Hidden native audio element */}
                <audio ref={audioRef} src={audioObjectUrl || undefined} preload="metadata" />
                {audioError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {audioError}
                    </div>
                )}

                {/* Custom Player Controls */}
                <div className="flex items-center gap-4 mb-4">
                    {/* Skip Back */}
                    <button
                        onClick={() => skipTime(-10)}
                        className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#e3dbcf] bg-[#f3ede3] hover:bg-[#e3dbcf] text-[#6b635a] transition-all"
                        title="Rewind 10s"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>
                    </button>

                    {/* Play/Pause */}
                    <button
                        onClick={togglePlay}
                        disabled={!audioObjectUrl}
                        className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1a1a1a] hover:bg-[#000] text-[#fcfbfa] transition-all shadow-sm"
                        title={isPlaying ? 'Pause' : 'Play'}
                    >
                        {isPlaying ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                        )}
                    </button>

                    {/* Skip Forward */}
                    <button
                        onClick={() => skipTime(10)}
                        className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#e3dbcf] bg-[#f3ede3] hover:bg-[#e3dbcf] text-[#6b635a] transition-all"
                        title="Forward 10s"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 19 7-7-7-7" /><path d="M5 12h14" /></svg>
                    </button>

                    {/* Speed Button */}
                    <button
                        onClick={cycleSpeed}
                        className="px-3 h-9 flex items-center justify-center rounded-lg border border-[#e3dbcf] bg-[#f3ede3] hover:bg-[#e3dbcf] text-[#6b635a] text-xs font-bold tracking-wide transition-all"
                        title="Change playback speed"
                    >
                        {playbackRate}x
                    </button>

                    <a
                        href={audioObjectUrl || undefined}
                        download={audioDownloadName}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-all ${audioObjectUrl ? 'border-[#e3dbcf] bg-[#f3ede3] hover:bg-[#e3dbcf] text-[#6b635a]' : 'border-[#e3dbcf] bg-[#f3ede3] text-[#b8b0a5] pointer-events-none'}`}
                        title="Download recording"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 3v12" />
                            <path d="m7 10 5 5 5-5" />
                            <path d="M5 21h14" />
                        </svg>
                    </a>

                    {/* Time Display */}
                    <div className="ml-auto text-xs text-[#8c8578] font-mono tracking-wider">
                        {formatTimestamp(currentTime)} / {formatTimestamp(duration)}
                    </div>
                </div>

                {/* Progress Bar */}
                <div
                    className="w-full h-2 bg-[#e3dbcf] rounded-full cursor-pointer mb-6 overflow-hidden"
                    onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const fraction = (e.clientX - rect.left) / rect.width;
                        if (audioRef.current) {
                            audioRef.current.currentTime = fraction * duration;
                        }
                    }}
                >
                    <div
                        className="h-full bg-[#1a1a1a] rounded-full transition-all duration-100"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Transcript Section */}
                <div className="border-t border-[#e3dbcf] pt-5">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold text-[#8c8578] uppercase tracking-widest">Transcript</span>
                        <button
                            onClick={generateTranscript}
                            disabled={transcriptLoading}
                            className="px-4 py-2 bg-[#1a1a1a] text-[#fcfbfa] hover:bg-[#000] rounded-lg text-xs font-bold uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {transcriptLoading ? 'Transcribing...' : transcript.length > 0 ? 'Regenerate' : 'Generate Transcript'}
                        </button>
                    </div>

                    <div
                        ref={transcriptRef}
                        className="bg-[#f3ede3] border border-[#e3dbcf] rounded-xl max-h-72 overflow-y-auto"
                    >
                        {transcriptLoading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <svg className="animate-spin h-7 w-7 text-[#1a1a1a] mb-3" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span className="text-[#8c8578] text-sm">Generating transcript...</span>
                            </div>
                        ) : transcriptError ? (
                            <div className="p-6 text-center">
                                <p className="text-red-600 text-sm mb-1">{transcriptError}</p>
                                <p className="text-[#8c8578] text-xs">Try again or check the recording.</p>
                            </div>
                        ) : transcript.length > 0 ? (
                            transcript.map((seg, i) => (
                                <div
                                    key={i}
                                    data-segment={i}
                                    onClick={() => seekTo(seg.time)}
                                    className={`flex px-4 py-3 border-b border-[#e3dbcf] last:border-b-0 cursor-pointer transition-colors ${i === activeSegmentIndex ? 'bg-[#e3dbcf]/70' : 'hover:bg-[#e3dbcf]/40'
                                        }`}
                                >
                                    <span className="text-[#1a1a1a] font-mono text-xs font-bold min-w-[48px] mr-3 mt-0.5">{formatTimestamp(seg.time)}</span>
                                    <span className="text-[#1a1a1a] text-sm leading-relaxed">{seg.text}</span>
                                </div>
                            ))
                        ) : (
                            <div className="p-10 text-center text-[#8c8578] text-sm">
                                Click "Generate Transcript" to transcribe this call
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
